import React, { useMemo } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Chip, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { formatDateTime, getUniqueMatchLabels } from './helpers';
import type { SimilarAccountRow } from './types';

export default function useResultsColumns() {
  return useMemo<Array<GridColDef<SimilarAccountRow>>>(() => {
    return [
      {
        field: 'fullName',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Stack spacing={0.5}>
            <Link to={`/users/${params.row.idMember}`} target="_blank" rel="noopener noreferrer">
              <Typography variant="body1" fontWeight={params.row.fullName ? 600 : 400}>
                {params.row.fullName ?? 'Unknown name'}
              </Typography>
            </Link>
            <Typography variant="body2" color="text.secondary">
              {params.row.idMember}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 0.8,
        minWidth: 220,
        renderCell: (params) => {
          return params.row.email;
        },
      },
      {
        field: 'phoneNumber',
        headerName: 'Phone',
        flex: 0.6,
        minWidth: 120,
        renderCell: (params) =>
          params.row.phoneNumber ? (
            <Typography variant="body2">{params.row.phoneNumber}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'brand',
        headerName: 'Brand',
        width: 80,
        renderCell: (params) =>
          params.row.brand ? (
            <Typography variant="body2">{params.row.brand}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'ordersCount',
        headerName: 'Orders',
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        width: 80,
        renderCell: (params) => {
          const value = params.row.ordersCount;

          if (typeof value === 'number') {
            return (
              <Typography variant="body2" sx={{ width: '100%', textAlign: 'right' }}>
                {value.toLocaleString()}
              </Typography>
            );
          }

          return (
            <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'right' }}>
              —
            </Typography>
          );
        },
      },
      {
        field: 'postcode',
        headerName: 'Postcode',
        width: 100,
        renderCell: (params) =>
          params.row.postcode ? (
            <Typography variant="body2">{params.row.postcode}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 150,
        renderCell: (params) => {
          const formatted = formatDateTime(params.row.createdAt);
          return formatted ? (
            <Typography variant="body2">{formatted}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last Login',
        minWidth: 150,
        renderCell: (params) => {
          const formatted = formatDateTime(params.row.lastLoginAt);
          return formatted ? (
            <Typography variant="body2">{formatted}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: 'matchedAttributes',
        headerName: 'Matching Attributes',
        width: 420,
        minWidth: 420,
        sortable: false,
        renderCell: (params) => {
          const uniqueLabels = getUniqueMatchLabels(params.row.matchedAttributes);
          return (
            <Stack direction="row" spacing={1} flexWrap="nowrap" overflow="hidden">
              {uniqueLabels.map((label) => {
                const key = `${params.row.id}-${label}`;
                return <Chip key={key} label={label} />;
              })}
            </Stack>
          );
        },
      },
    ];
  }, []);
}
